import { useState } from 'react';

interface FigureGalleryProps {
  figures: any[];
  isAdmin?: boolean;
  currentUser?: any;
  userProfiles?: Record<string, string>;
  onUpdate?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (id: string) => void;
}

function UserBadge({ username }: { username?: string }) {
  if (!username) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium border border-teal-100 dark:border-teal-700 whitespace-nowrap">
      <i className="ri-user-line text-xs"></i>
      {username}
    </span>
  );
}

export default function FigureGallery({
  figures,
  isAdmin = false,
  currentUser,
  userProfiles = {},
  onUpdate,
  onEdit,
  onDelete,
}: FigureGalleryProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedFigure, setSelectedFigure] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const categories = ['概念図', 'データ構造', '記述統計', '推定結果'];

  const filteredFigures =
    selectedCategory === 'all'
      ? figures
      : figures.filter((fig) => fig.category === selectedCategory);

  const canModify = (item: any) => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return item.user_id === currentUser.id;
  };

  const handleDelete = (id: string) => {
    if (onDelete) {
      onDelete(id);
      setDeleteConfirm(null);
      setSelectedFigure(null);
    }
  };

  // Empty state
  if (figures.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-image-line text-teal-600 dark:text-teal-400"></i>
          </div>
          図表ギャラリー
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">図表がまだ登録されていません。</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header & Category selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-image-line text-teal-600 dark:text-teal-400"></i>
          </div>
          図表ギャラリー
        </h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            すべて
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredFigures.map((figure) => {
          const userCanModify = canModify(figure);
          const username = figure.user_profiles?.username || null;

          return (
            <div
              key={figure.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all group"
            >
              <div
                onClick={() => setSelectedFigure(figure)}
                className="aspect-video bg-gray-100 dark:bg-gray-700 relative overflow-hidden cursor-pointer"
              >
                <img
                  src={figure.file_or_link}
                  alt={figure.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/40 px-2 py-0.5 rounded">
                    {figure.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedFigure(figure)}
                      className="w-7 h-7 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer"
                      title="詳細"
                    >
                      <i className="ri-zoom-in-line text-sm"></i>
                    </button>
                    {userCanModify && onEdit && (
                      <button
                        onClick={() => onEdit(figure)}
                        className="w-7 h-7 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer"
                        title="編集"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    )}
                    {userCanModify && onDelete && (
                      <button
                        onClick={() => setDeleteConfirm(figure.id)}
                        className="w-7 h-7 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                        title="削除"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>

                <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-1.5 line-clamp-2">
                  {figure.title}
                </h3>
                {username && (
                  <div className="mb-1.5">
                    <UserBadge username={username} />
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {figure.purpose}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* No figures for selected category */}
      {filteredFigures.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <i className="ri-image-off-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
          <p className="text-gray-500 dark:text-gray-400 text-sm">選択したカテゴリに該当する図表がありません。</p>
        </div>
      )}

      {/* Figure detail modal */}
      {selectedFigure && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFigure(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-base font-bold text-gray-800 dark:text-white">{selectedFigure.title}</h3>
                {selectedFigure.user_profiles?.username && (
                  <div className="mt-1">
                    <UserBadge username={selectedFigure.user_profiles.username} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canModify(selectedFigure) && onEdit && (
                  <button
                    onClick={() => { onEdit(selectedFigure); setSelectedFigure(null); }}
                    className="w-8 h-8 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer"
                    title="編集"
                  >
                    <i className="ri-edit-line text-base"></i>
                  </button>
                )}
                {canModify(selectedFigure) && onDelete && (
                  <button
                    onClick={() => setDeleteConfirm(selectedFigure.id)}
                    className="w-8 h-8 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                    title="削除"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>
                )}
                <button
                  onClick={() => setSelectedFigure(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-600">
                <img src={selectedFigure.file_or_link} alt={selectedFigure.title} className="w-full h-auto" />
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">カテゴリ</h4>
                  <span className="inline-block bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 px-3 py-1 rounded-full text-xs font-medium">
                    {selectedFigure.category}
                  </span>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">目的</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{selectedFigure.purpose}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">読み方</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{selectedFigure.how_to_read}</p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider mb-2">暫定解釈</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{selectedFigure.tentative_interpretation}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">出典</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedFigure.data_source}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full">
                <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">削除の確認</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">この操作は取り消せません</p>
              </div>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">この図表を削除してもよろしいですか？</p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}